import { Modal2 } from "@iq/models";
import { useEffect, useMemo, useState } from "react";
import ServiceBuilder from "../ServiceBuilder/ServiceBuilder";
import { getProducts, getPaymentConfig } from "../../services/OnlineStoreService";
import {
    buildFollowUpPayload,
    createFollowUpAppointment,
    fetchFollowUpAvailability,
} from "../../services/followUpService";
import { formatDuration, formatPrice } from "../../utils/pricing";
import { formatTime } from "../../utils/time";
import { requiredDeposit } from "../../utils/deposit";
import { canSaveSelections, getProductAttributes, getSelectedServiceDetails } from "../../utils/serviceBuilder";
import styles from "./followUpBookingModal.module.css";
import LoadingScreen from "../LoadingScreen/LoadingScreen";

const STEPS = ["Services", "Time", "Review"];

function dateKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function addMinutes(time, duration) {
    const [hours, minutes] = String(time).split(":").map(Number);
    const total = hours * 60 + minutes + duration;
    return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function attributeLabels(details) {
    return details.selected_attributes.map((attribute) => `${attribute.attribute_name}: ${attribute.attribute_value}`);
}

function formatDate(value) {
    if (!value) return "Choose a date";
    return new Intl.DateTimeFormat("en-US", {
        weekday: "short",
        month: "long",
        day: "numeric",
        year: "numeric",
    }).format(new Date(`${value}T12:00:00`));
}

function FollowUpBookingModal({ isOpen, appointment, userID, onClose, onBooked }) {
    const [step, setStep] = useState(0);
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [building, setBuilding] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dateKey());
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [depositThreshold, setDepositThreshold] = useState(null);
    const [depositError, setDepositError] = useState("");

    const totalDuration = cart.reduce((sum, item) => sum + item.details.total_duration, 0);
    const totalPrice = cart.reduce((sum, item) => sum + item.details.total_price, 0);

    const builderAttributes = useMemo(() => getProductAttributes(building?.product), [building?.product]);
    const builderDetails = useMemo(
        () => (building ? getSelectedServiceDetails(building.product, building.selections) : null),
        [building],
    );
    const canSaveBuilder = Boolean(building) && canSaveSelections(builderAttributes, building.selections);
    const summaryItems = building && builderDetails
        ? (building.index >= 0
            ? cart.map((item, index) => index === building.index
                ? { product: building.product, details: builderDetails, draft: true }
                : item)
            : [...cart, { product: building.product, details: builderDetails, draft: true }])
        : cart;
    const summaryDuration = summaryItems.reduce((sum, item) => sum + item.details.total_duration, 0);
    const summaryPrice = summaryItems.reduce((sum, item) => sum + item.details.total_price, 0);
    let summaryDeposit = null;
    let depositPreviewError = depositError;
    if (depositThreshold !== null) {
        try {
            summaryDeposit = requiredDeposit(summaryPrice, depositThreshold);
        } catch (previewError) {
            depositPreviewError = previewError.message;
        }
    }

    useEffect(() => {
        if (!isOpen) return;

        setStep(0);
        setCart([]);
        setBuilding(null);
        setSelectedDate(dateKey());
        setSelectedSlot("");
        setSlots([]);
        setError("");
        setDepositThreshold(null);
        setDepositError("");
        setLoading(true);

        let active = true;
        Promise.all([getProducts(), getPaymentConfig()])
            .then(([productList, config]) => {
                requiredDeposit(0, config?.deposit_amount);
                if (!active) return;
                setProducts(productList);
                setDepositThreshold(config.deposit_amount);
            })
            .catch((requestError) => {
                if (active) setDepositError(requestError.message);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => { active = false; };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || step !== 1 || !selectedDate || totalDuration <= 0) return;

        let active = true;
        setLoading(true);
        setError("");
        setSelectedSlot("");

        fetchFollowUpAvailability(selectedDate.slice(0, 7), totalDuration, selectedDate, userID)
            .then((payload) => {
                if (!active) return;
                const day = (payload?.availability || []).find((entry) => entry.date === selectedDate);
                setSlots(Array.isArray(day?.available_slots) ? day.available_slots : []);
            })
            .catch((requestError) => {
                if (active) setError(requestError.message);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [isOpen, selectedDate, step, totalDuration, userID]);

    function openBuilder(product) {
        const index = cart.findIndex((item) => item.product.id === product.id);
        setBuilding(index >= 0 ? { ...cart[index], index } : { product, selections: {}, index: -1 });
    }

    function saveBuilder() {
        const item = { product: building.product, selections: building.selections, details: builderDetails };
        setCart((current) => (building.index >= 0
            ? current.map((entry, index) => (index === building.index ? item : entry))
            : [...current, item]));
        setBuilding(null);
    }

    function removeService(index) {
        setCart((current) => current.filter((_, entryIndex) => entryIndex !== index));
    }

    async function submitFollowUp() {
        if (!appointment?.customer?.id || cart.length === 0 || !selectedSlot) return;
        if (depositThreshold === null || depositPreviewError) {
            setDepositError(depositPreviewError || "Deposit configuration is not available. Close and reopen to retry.");
            return;
        }

        setLoading(true);
        setError("");
        setSubmitting(true);
        try {
            const created = await createFollowUpAppointment(buildFollowUpPayload({
                userID,
                customerID: appointment.customer.id,
                date: selectedDate,
                startTime: selectedSlot,
                endTime: addMinutes(selectedSlot, totalDuration),
                services: cart.map(({ details }) => ({
                    product_id: details.product_id,
                    appointment_attributes: details.attribute_selection,
                })),
            }));
            await onBooked?.(created);
            onClose();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    }

    function actionButtons() {
        const back = { key: "back", text: "Back", color: "light", className: styles.footerAction, onClick: () => setStep(step - 1) };

        if (step === 0 && building) {
            return [
                { key: "back", text: "Back", color: "light", className: styles.footerAction, onClick: () => setBuilding(null) },
                { key: "save", text: building.index >= 0 ? "Update service" : "Add service", className: styles.footerAction, disabled: !canSaveBuilder, onClick: saveBuilder },
            ];
        }
        if (step === 0) {
            return [
                { key: "close", text: "Close", color: "light", className: styles.footerAction, onClick: onClose },
                { key: "next", text: "Choose time", className: styles.footerAction, disabled: cart.length === 0, onClick: () => setStep(1) },
            ];
        }
        if (step === 1) {
            return [back, { key: "next", text: "Review", className: styles.footerAction, disabled: !selectedSlot, onClick: () => setStep(2) }];
        }
        return [back, { key: "book", text: loading ? "Booking..." : "Book follow-up", className: styles.footerAction, disabled: loading || summaryDeposit === null || Boolean(depositPreviewError), onClick: submitFollowUp }];
    }

    if (!isOpen) return null;

    const customerName = appointment?.customer?.name || "customer";

    return (
        <Modal2
            isOpen={isOpen}
            onClose={onClose}
            title="Book follow-up"
            actionButtons={actionButtons()}
            errorMessage={error || depositPreviewError}
            styles={{
                panel: {
                    height: "clamp(420px, 90vh, 980px)",
                    width: "clamp(800px, 75vw, 1200px)",
                },
            }}
        >
            <LoadingScreen open={submitting} message="Booking follow-up..." />
            <div className={styles.modal}>
                <div className={styles.splitLayout}>
                    <section className={styles.workflow}>
                        <div className={styles.workflowHeader}>
                            <div>
                                <p className={styles.eyebrow}>Follow-up appointment</p>
                                <p className={styles.appointmentContext}>
                                    <strong>{customerName}</strong>
                                    <span>{STEPS[step]} · Step {step + 1} of {STEPS.length}</span>
                                </p>
                            </div>
                        </div>

                        <div className={styles.progress}>
                            {STEPS.map((label, index) => (
                                <div key={label} className={`${styles.progressItem} ${index <= step ? styles.progressActive : ""}`}>
                                    <span>{index + 1}</span>{label}
                                </div>
                            ))}
                        </div>

                        <div className={styles.workflowContent}>
                            {step === 0 && building && (
                                <div className={styles.section}>
                                    <div className={styles.sectionIntro}>
                                        <h2>Choose service options</h2>
                                        <p>Personalize {building.product.name} for this visit.</p>
                                    </div>
                                    <div className={styles.builderHeader}>
                                        <div>
                                            <strong>{building.product.name}</strong>
                                            <p className={styles.muted}>
                                                Starts at {formatPrice(building.product.base_price)} · {formatDuration(building.product.base_duration)}
                                            </p>
                                        </div>
                                        <strong>{formatPrice(builderDetails?.total_price)}</strong>
                                    </div>
                                    <ServiceBuilder
                                        product={building.product}
                                        selections={building.selections}
                                        onChange={(next) => setBuilding((current) => ({ ...current, selections: next }))}
                                    />
                                </div>
                            )}

                            {step === 0 && !building && (
                                <div className={styles.section}>
                                    <div className={styles.sectionIntro}>
                                        <h2>Add services</h2>
                                        <p>Choose the services to include in the follow-up.</p>
                                    </div>
                                    <div className={styles.productGrid}>
                                        {loading ? <p className={styles.muted}>Loading services...</p> : products.map((product) => {
                                            const added = cart.some((item) => item.product.id === product.id);
                                            return (
                                                <button
                                                    key={product.id}
                                                    type="button"
                                                    className={`${styles.card} ${added ? styles.cardAdded : ""}`}
                                                    onClick={() => openBuilder(product)}
                                                >
                                                    <strong>{product.name}</strong>
                                                    <span className={styles.cardDescription}>{product.description || "Service"}</span>
                                                    <small>{added ? "Added · Edit" : `${formatPrice(product.base_price)} · ${formatDuration(product.base_duration)}`}</small>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {cart.length > 0 ? (
                                        <div className={styles.cart}>
                                            <h3>Selected services</h3>
                                            {cart.map((item, index) => (
                                                <div key={item.product.id} className={styles.cartItem}>
                                                    <div className={styles.row}>
                                                        <strong>{item.product.name}</strong>
                                                        <span>{formatPrice(item.details.total_price)}</span>
                                                    </div>
                                                    {attributeLabels(item.details).map((label) => (
                                                        <small key={label} className={styles.muted}>{label}</small>
                                                    ))}
                                                    <div className={styles.cartActions}>
                                                        <button type="button" onClick={() => openBuilder(item.product)}>Edit</button>
                                                        <button type="button" onClick={() => removeService(index)}>Remove</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {step === 1 && (
                                <div className={styles.section}>
                                    <div className={styles.sectionIntro}>
                                        <h2>Choose a time</h2>
                                        <p>Pick a date to see available appointment times.</p>
                                    </div>
                                    <label className={styles.dateField}>
                                        <span>Date</span>
                                        <input type="date" min={dateKey()} value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
                                    </label>
                                    <p className={styles.availabilityLabel}>Available times · {formatDuration(totalDuration)}</p>
                                    <div className={styles.slotGrid}>
                                        {loading ? <p className={styles.muted}>Checking availability...</p> : slots.length ? slots.map((slot) => (
                                            <button
                                                key={slot}
                                                type="button"
                                                aria-pressed={selectedSlot === slot}
                                                className={`${styles.slot} ${selectedSlot === slot ? styles.slotSelected : ""}`}
                                                onClick={() => setSelectedSlot(slot)}
                                            >
                                                {formatTime(slot)}
                                            </button>
                                        )) : <p className={styles.muted}>No times available on this date.</p>}
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <div className={styles.section}>
                                    <div className={styles.sectionIntro}>
                                        <h2>Review follow-up</h2>
                                        <p>Confirm the services and time before booking.</p>
                                    </div>
                                    <div className={styles.reviewSummary}>
                                        <div className={styles.reviewLine}><span>Customer</span><strong>{customerName}</strong></div>
                                        <div className={styles.reviewLine}><span>Date</span><strong>{formatDate(selectedDate)}</strong></div>
                                        <div className={styles.reviewLine}><span>Time</span><strong>{formatTime(selectedSlot)} – {formatTime(addMinutes(selectedSlot, totalDuration))}</strong></div>
                                        <div className={styles.reviewLine}><span>Duration</span><strong>{formatDuration(totalDuration)}</strong></div>
                                        <div className={`${styles.reviewLine} ${styles.reviewTotal}`}><span>Total</span><strong>{formatPrice(totalPrice)}</strong></div>
                                        {summaryDeposit !== null ? (
                                            <>
                                                <div className={styles.reviewLine}><span>Deposit at confirmation</span><strong>{formatPrice(summaryDeposit)}</strong></div>
                                                <div className={styles.reviewLine}><span>Balance at completion</span><strong>{formatPrice(totalPrice - summaryDeposit)}</strong></div>
                                                <p>{summaryDeposit === 0
                                                    ? "No deposit is required. Confirm the appointment, then collect the full balance at completion."
                                                    : "The deposit is collected when the appointment is confirmed, not when this follow-up is booked."}</p>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    <aside className={styles.summaryPanel} aria-label="Follow-up summary">
                        <div className={styles.summaryHeader}>
                            <p className={styles.eyebrow}>Booking summary</p>
                            <h2>{customerName}</h2>
                            <p>Follow-up appointment</p>
                        </div>

                        <div className={styles.summaryServices}>
                            <h3>Services · {summaryItems.length}</h3>
                            {summaryItems.length ? summaryItems.map((item) => (
                                <div className={styles.summaryService} key={item.product.id}>
                                    <div className={styles.row}>
                                        <strong>{item.product.name}</strong>
                                        <span>
                                            {item.draft ? <small className={styles.draftLabel}>Draft · </small> : null}
                                            {formatPrice(item.details.total_price)}
                                        </span>
                                    </div>
                                    {attributeLabels(item.details).length ? (
                                        <ul>
                                            {attributeLabels(item.details).map((label) => <li key={label}>{label}</li>)}
                                        </ul>
                                    ) : null}
                                </div>
                            )) : (
                                <p className={styles.muted}>Your selected services will appear here.</p>
                            )}
                        </div>

                        <div className={styles.summaryTotals}>
                            {summaryDeposit !== null ? (
                                <>
                                    <div className={styles.summaryRow}><span>Deposit at confirmation</span><strong>{formatPrice(summaryDeposit)}</strong></div>
                                    <div className={styles.summaryRow}><span>Balance at completion</span><strong>{formatPrice(summaryPrice - summaryDeposit)}</strong></div>
                                </>
                            ) : <p className={styles.muted}>Deposit preview unavailable</p>}
                            <div className={styles.summaryRow}><span>Date</span><strong>{formatDate(selectedDate)}</strong></div>
                            <div className={styles.summaryRow}><span>Time</span><strong>{selectedSlot ? `${formatTime(selectedSlot)} – ${formatTime(addMinutes(selectedSlot, totalDuration))}` : "Not selected"}</strong></div>
                            <div className={styles.summaryRow}><span>Duration</span><strong>{summaryDuration ? formatDuration(summaryDuration) : "—"}</strong></div>
                            <div className={styles.summaryGrandTotal}>
                                <span>Follow-up total</span>
                                <strong>{formatPrice(summaryPrice)}</strong>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </Modal2>
    );
}

export default FollowUpBookingModal;
